/**
 * C5 — partner enablement pack composer.
 *
 * Pure function: (tco-compare data + deal context) → a section-based
 * document the hosts render (GAS writes a Sheet, TekStak downloads
 * markdown). Composition lives HERE so the redaction rules are
 * testable, not scattered across renderers (risk register #4):
 *
 *   - Street intel ALWAYS renders as "estimated street" band language.
 *     Source names/notes NEVER enter the pack, nda_bound or not — the
 *     pack is a shareable artifact and provenance beyond
 *     type/date/confidence is not the partner's to forward.
 *   - PAN-internal economics NEVER enter the pack: no PAN net, no
 *     internal ask, no disti tier. The partner sees the customer
 *     price, their own buy price, and their own margin.
 *   - Unpinned auto-matched SKUs keep their "candidate — verify"
 *     label; unverified competitor figures render "(vendor-claimed)".
 *
 * The pack is the flywheel artifact: incumbent picture → side-by-side
 * TCO → the story → discovery script → registration pitch.
 */

import type {
  TcoCompareResponse,
  ValueStory,
  PartnerPackDoc,
  PackSection,
} from '../contracts/competitive.js';
import type { QuoteLineItem, QuoteScenario } from '../contracts/quoting.js';
import type { VendorFootprint } from '../contracts/procurement.js';
import { computeQuoteEconomics, computeTcoRoll, round2 } from './engine.js';

export interface PartnerPackInput {
  data: TcoCompareResponse;
  horizonYears?: number;
  deal?: {
    sfOpportunityId?: string | null;
    deptAcronym?: string | null;
    partnerName?: string | null;
    sapNumber?: string | null;
    registrationStatus?: string | null;
  };
  /** C0 incumbent strip data (public SCPRS) for the incumbent picture. */
  incumbents?: VendorFootprint[];
  formatCurrency?: (n: number) => string;
  /** Stamp the generation date (callers pass it; the composer stays pure). */
  generatedOn?: string;
}

const FLAT: QuoteScenario = { name: 'list', customerDiscountPct: 0, partnerMarginPct: 0, distiMarginPct: 0 };

function buildCompetitorLines(data: TcoCompareResponse, discount: number): QuoteLineItem[] {
  const out: QuoteLineItem[] = [];
  data.stack.forEach((entry, i) => {
    entry.proposed.forEach((p, j) => {
      if (p.listUnit === null) return;
      out.push({
        lineId: `c-${i}-${j}`,
        sku: p.sku,
        description: p.name,
        productFamily: null,
        qty: entry.qty * (p.qtyPerUnit ?? 1),
        termMonths: p.termMonths,
        listUnit: p.listUnit,
        contractUnit: round2(p.listUnit * (1 - discount)),
        priceResolution: 'explicit',
        discountCategory: null,
        contractId: 'competitor',
        pricingAsOf: null,
      });
    });
  });
  return out;
}

function fillTrack(track: string, vendor: string, savings: number | null, horizon: number, fmt: (n: number) => string): string {
  return track
    .replace(/\{vendor\}/g, vendor)
    .replace(/\{horizon\}/g, String(horizon))
    .replace(/\{savings\}/g, savings !== null && savings > 0 ? fmt(savings) : 'the computed delta (see the TCO section)');
}

/** "estimated street" phrasing — the ONLY way intel enters the pack. */
function streetLanguage(data: TcoCompareResponse): string | null {
  const best = data.streetIntel.best;
  if (!best) return null;
  return `partner-sourced estimate, as of ${best.asOfDate}, ${best.confidence} confidence${best.isStale ? ' — STALE, reconfirm before use' : ''}`;
}

export function composePartnerPack(input: PartnerPackInput): PartnerPackDoc {
  const { data, deal, incumbents } = input;
  const horizon = Math.max(1, Math.round(input.horizonYears ?? 5));
  const fmt = input.formatCurrency ?? ((n: number) => '$' + Math.round(n).toLocaleString('en-US'));
  const scenario = data.panScenario ?? FLAT;

  const panRoll = computeTcoRoll(data.panLines, scenario, { horizonYears: horizon });
  const econ = computeQuoteEconomics(data.panLines, scenario);
  const compListRoll = computeTcoRoll(buildCompetitorLines(data, 0), FLAT, { horizonYears: horizon });
  const best = data.streetIntel.best;
  const streetLow = best ? computeTcoRoll(buildCompetitorLines(data, best.discountPctMax), FLAT, { horizonYears: horizon }) : null;
  const streetHigh = best ? computeTcoRoll(buildCompetitorLines(data, best.discountPctMin), FLAT, { horizonYears: horizon }) : null;
  const delta = round2(compListRoll.horizonTotal - panRoll.horizonTotal);

  const vendorStories = data.valueStories.filter((s) => s.competitorVendor !== null);
  const platformStory = data.valueStories.find((s) => s.architecture === 'platform') ?? null;
  const leadStory: ValueStory | null = vendorStories[0] ?? platformStory;

  const sections: PackSection[] = [];

  // 1 — The play
  if (leadStory) {
    const paras: string[] = [];
    if (leadStory.talkTrack) paras.push(fillTrack(leadStory.talkTrack, data.vendor, delta, horizon, fmt));
    if (leadStory.whyDoAnything) paras.push(`Why do anything? ${leadStory.whyDoAnything}`);
    if (leadStory.whyNow) paras.push(`Why now? ${leadStory.whyNow}`);
    if (leadStory.whyUs) paras.push(`Why with us? ${leadStory.whyUs}`);
    sections.push({ heading: `The play — ${leadStory.headline}`, kind: 'paragraphs', items: paras });
    if (leadStory.differentiators.length) {
      sections.push({ heading: 'What they can’t do', kind: 'bullets', items: leadStory.differentiators });
    }
  }

  // 2 — Incumbent picture (public SCPRS data — shareable)
  if (incumbents?.length) {
    sections.push({
      heading: 'Seated incumbents at this agency (public SCPRS record)',
      kind: 'table',
      rows: [
        ['Vendor', 'Spend (window)', 'Categories', 'Next contract expiry'],
        ...incumbents.slice(0, 6).map((v) => [
          v.vendor === data.vendor ? `${v.vendor} ◀ target` : v.vendor,
          v.spendFormatted,
          (v.categories ?? []).slice(0, 3).join(', ') || '—',
          v.nextExpiry ?? '—',
        ]),
      ],
    });
  }

  // 3 — Side-by-side TCO
  const street = streetLanguage(data);
  const tcoRows: string[][] = [
    ['', 'One-time', 'Recurring / yr', `${horizon}-yr total`],
    ['Ours — customer pricing', fmt(panRoll.oneTimeTotal), fmt(panRoll.recurringAnnual), fmt(panRoll.horizonTotal)],
    [`${data.vendor} — CA price-book list`, fmt(compListRoll.oneTimeTotal), fmt(compListRoll.recurringAnnual), fmt(compListRoll.horizonTotal)],
  ];
  if (streetLow && streetHigh && street) {
    tcoRows.push([`${data.vendor} — estimated street (${street})`, '', '', `${fmt(streetLow.horizonTotal)}–${fmt(streetHigh.horizonTotal)}`]);
  }
  sections.push({ heading: `${horizon}-year TCO — ours vs ${data.vendor}`, kind: 'table', rows: tcoRows });
  sections.push({
    kind: 'paragraphs',
    heading: '',
    items: [delta >= 0
      ? `The platform construction saves an estimated ${fmt(delta)} against ${data.vendor}’s list over ${horizon} years.`
      : `${data.vendor}’s list runs ${fmt(-delta)} under our customer price over ${horizon} years — lead with the platform story and the operational costs their number leaves out.`],
  });

  // 4 — The stack
  if (data.stack.length) {
    sections.push({
      heading: 'The stack, rung by rung',
      kind: 'table',
      rows: [
        ['Ours', 'Theirs', 'Their SKUs (as resolved)', 'Their list (ext.)'],
        ...data.stack.map((s) => [
          `${s.panModel} ×${s.qty}`,
          `${s.competitorModel}${(s.normalizer as any)?.competitor?.verified === false ? ' (vendor-claimed)' : ''}`,
          s.proposed.length
            ? s.proposed.map((p) => `${p.sku}${p.pinned ? '' : ' (candidate — verify)'}${p.termMonths ? ` · ${p.termMonths} mo` : ''}`).join('; ')
            : 'no price-book match',
          s.proposed.map((p) => (p.listUnit !== null ? fmt(p.listUnit * s.qty * (p.qtyPerUnit ?? 1)) : '—')).join('; ') || '—',
        ]),
      ],
    });
  }

  // 5 — Discovery script (traps from every story in play)
  const traps = data.valueStories.flatMap((s) => s.trapQuestions ?? []);
  if (traps.length) {
    sections.push({ heading: 'Discovery questions to plant', kind: 'bullets', items: [...new Set(traps)] });
  }

  // 6 — Objection prep
  const objections = data.valueStories.flatMap((s) => s.objectionHandles ?? []);
  if (objections.length) {
    sections.push({
      heading: 'Objection prep',
      kind: 'bullets',
      items: objections.map((o) => `“${o.objection}” — ${fillTrack(o.response, data.vendor, delta, horizon, fmt)}`),
    });
  }

  // 7 — Registration & partner economics (the partner's OWN numbers only)
  const regStatus = deal?.registrationStatus && deal.registrationStatus !== 'none' ? deal.registrationStatus : 'not yet requested';
  sections.push({
    heading: 'Registration & your economics',
    kind: 'bullets',
    items: [
      `Customer total (as constructed): ${fmt(econ.customerTotal)}`,
      `Your buy: ${fmt(econ.partnerBuyTotal)} — margin ${fmt(econ.marginByTier.partner.amount)} (${Math.round(econ.marginByTier.partner.pct * 1000) / 10}%)`,
      `Deal registration: ${regStatus}${deal?.sapNumber ? ` · SAP ${deal.sapNumber}` : ''}`,
      'Run the play — position the platform, set the traps in discovery, bring the TCO — and the registration protects the deal as yours.',
    ],
  });

  // Footer — assumptions + provenance
  const storyVersions = data.valueStories.map((s) => `${s.architecture}${s.competitorVendor ? `/${s.competitorVendor}` : ''} v${s.version}`);
  const footer = [
    ...panRoll.assumptions,
    ...data.assumptions,
    ...data.unresolved.map((u) => `Unresolved: ${u.detail}`),
    `Value story versions: ${storyVersions.join(', ') || 'none'}.`,
    `Competitor list prices from California public price books (SLP/CMAS/ETC record).${input.generatedOn ? ` Generated ${input.generatedOn}.` : ''}`,
    'Partner-confidential. Estimated street figures are estimates, not quotes.',
  ];

  return {
    title: `Partner pack — ${deal?.deptAcronym ?? 'deal'} vs ${data.vendor}`,
    subtitle: [
      deal?.sfOpportunityId ? `Deal ${deal.sfOpportunityId}` : 'Unlinked deal',
      deal?.deptAcronym ?? null,
      deal?.partnerName ? `prepared with ${deal.partnerName}` : null,
      `${horizon}-year view`,
    ].filter(Boolean).join(' · '),
    confidentiality: 'PARTNER CONFIDENTIAL — for deal registration and positioning; not a quote.',
    sections,
    footer,
  };
}
